import { useMemo, useState, useEffect } from 'react'
import useSyncShipmentsByPOs from 'services/syncShipmentsByPOs'
import { useQueryClient } from '@tanstack/react-query'
import FlexBox from 'components/FlexBox'
import { Acknowledgement } from 'components/Acknowledgement/Acknowledement'
import ModifiedShipmentsTable from './modified'
import { useShipmentManagementContext } from 'context/ShipmentManagementContext'
import { findInvalidBookingVersion } from './utils'
import { useGlobalContext } from 'context/GlobalContext'
import {
  PurchaseOrderInterface,
  TloAlert,
  ShipmentRowInterface,
} from '../../../common/types'
import { Button, Heading, List } from '@enterprise-ui/canvas-ui-react'
import {
  useShipmentsModificationQuery,
  useShipmentsLoadModificationQuery,
  useShipmentsCancellationsQuery,
} from '../../../hooks/reactQuery/postQueryHooks'
import { useShipmentsByTloAlerts } from 'hooks/reactQuery/getQueryHooks'
import { useAnalyticsWrapper } from 'hooks/useAnalyticsWrapper'
import { useNavigate } from 'react-router-dom'
import { shipmentsModified } from 'components/Shipments/utils'
import { isTloOnlyRemoval } from 'services/shipmentModifications'
import { uniqByProp } from 'common/utils'
import { TloWarnings } from './TloWarnings'
import { TloSummaryCard } from './TloSummaryCard'

interface ShipmentWithFailureStatus extends ShipmentRowInterface {
  failureStatus?: string
}

export interface AllShipmentAdjustment {
  isComplete: boolean
  failedAdjustments: ShipmentWithFailureStatus[]
  routingConflictFailures: ShipmentWithFailureStatus[]
  otherFailures: ShipmentWithFailureStatus[]
}

interface SubmitChangesProps {
  onClose?: () => void
}

const SubmitChanges = ({ onClose }: SubmitChangesProps) => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const analyticsWrapper = useAnalyticsWrapper()
  const [isSearchTloSubmit, setIsSearchTloSubmit] = useState<boolean>(false)
  const { shipmentManagementState } = useShipmentManagementContext()
  const { globalState, globalDispatch } = useGlobalContext()
  const vendorIds: number[] =
    globalState.filters.shipmentManagement.vendorIds || []

  const purchaseOrders: Array<PurchaseOrderInterface> = Object.values(
    shipmentManagementState.shipmentsByPurchaseOrderKey,
  )

  const shipmentsFromPurchaseOrders = purchaseOrders.reduce(
    (prev: Array<any>, curr: any) => {
      return [...prev, ...Object.values(curr)]
    },
    [],
  )
  const loadShipments: Array<PurchaseOrderInterface> = Object.values(
    shipmentManagementState.shipmentsByLoadIdKey,
  )

  const shipmentsFromLoads = Object.values(loadShipments).reduce(
    (prev: Array<any>, curr: any) => {
      return [...prev, ...Object.values(curr)]
    },
    [],
  )

  const shipmentsFromShipmentView =
    Object.values(shipmentManagementState.shipmentsViewData) || []

  const shipmentsCombined = [
    ...shipmentsFromPurchaseOrders.filter(shipmentsModified),
    ...shipmentsFromLoads.filter(shipmentsModified),
    ...shipmentsFromShipmentView.filter(shipmentsModified),
  ]
  // remove possible duplicates due to PO data sync
  const uniqueById = uniqByProp('bookingId')
  const shipments = uniqueById(shipmentsCombined)

  const vendors: number[] = shipments.reduce((acc, shipment) => {
    if (!acc.includes(shipment.vendorId)) {
      acc.push(shipment.vendorId)
    }
    return acc
  }, [])

  // True when every shipment in this submission is a TLO-only removal by an internal admin.
  // Used to adjust modal copy so "review" language is omitted.
  const isTloOnlyRemovalSubmit =
    shipments.length > 0 &&
    globalState.userInfo.isTgtInternalAdmin &&
    shipments.every((s) =>
      isTloOnlyRemoval(s, globalState.userInfo.isTgtInternalAdmin),
    )

  const tloShipmentAlerts = useShipmentsByTloAlerts(vendors, shipments)

  const hasRequiredEntitlements = globalState.userInfo.shipmentManagement.write
  const [syncSettings, setSyncSettings] = useState<{
    uid: number
    checkVersion: boolean
    hasRequiredEntitlements: boolean
    failedModifiedShipments: ShipmentWithFailureStatus[]
  }>({
    uid: 1,
    checkVersion: true,
    hasRequiredEntitlements,
    failedModifiedShipments: [],
  })

  // Show latest Shipments
  const { syncShipmentsByPOsState } = useSyncShipmentsByPOs(
    shipments,
    syncSettings,
  )

  const invalidateQueriesAndClose = () => {
    // avoid race condition that occasionally occurs if you refresh data immediately after posting modification
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['shipments-by-po'] })
      queryClient.invalidateQueries({ queryKey: ['shipments-by-filters'] })
      setIsSubmitting(false)
      handleClose()
    }, 2000)
  }

  const queryClient = useQueryClient()
  const {
    mutate: modifyShipments,
    data: modifyShipmentsResponse,
    isSuccess: modifyShipmentsIsSuccess,
    isError: modifyShipmentsIsError,
    isLoading: modifyShipmentsIsLoading,
  } = useShipmentsModificationQuery()
  const {
    mutate: modifyShipmentsLoad,
    data: modifyShipmentsLoadResponse,
    isSuccess: modifyShipmentsLoadIsSuccess,
    isError: modifyShipmentsLoadIsError,
    isLoading: modifyShipmentsLoadIsLoading,
  } = useShipmentsLoadModificationQuery()
  const {
    mutate: cancelShipments,
    data: cancelShipmentsResponse,
    isSuccess: cancelShipmentsIsSuccess,
    isError: cancelShipmentsIsError,
    isLoading: cancelShipmentsIsLoading,
  } = useShipmentsCancellationsQuery()
  const allShipmentAdjustments: AllShipmentAdjustment = useMemo(() => {
    return {
      isComplete: false,
      failedAdjustments: [],
      routingConflictFailures: [],
      otherFailures: [],
    }
  }, [])
  const handleClose = () => {
    if (onClose && hasFailures) {
      onClose()
    }
    globalDispatch({
      payload: null,
      type: 'SET_MODAL',
    })
  }
  if (
    modifyShipmentsIsSuccess &&
    modifyShipmentsLoadIsSuccess &&
    cancelShipmentsIsSuccess
  ) {
    allShipmentAdjustments.isComplete = true
    allShipmentAdjustments.failedAdjustments = [
      ...(modifyShipmentsResponse || []),
      ...(modifyShipmentsLoadResponse || []),
      ...(cancelShipmentsResponse || []),
    ]

    allShipmentAdjustments.routingConflictFailures =
      allShipmentAdjustments.failedAdjustments.filter(
        (shipment: ShipmentWithFailureStatus) =>
          shipment.failureStatus === 'SHIPMENT_ROUTING_STATUS_CONFLICT',
      )
    allShipmentAdjustments.otherFailures =
      allShipmentAdjustments.failedAdjustments.filter(
        (shipment: ShipmentWithFailureStatus) =>
          shipment.failureStatus &&
          shipment.failureStatus !== 'SHIPMENT_ROUTING_STATUS_CONFLICT',
      )

    if (allShipmentAdjustments.failedAdjustments.length === 0) {
      if (!isSearchTloSubmit) {
        invalidateQueriesAndClose()
      }
    }
  }
  if (
    modifyShipmentsIsError &&
    modifyShipmentsLoadIsError &&
    cancelShipmentsIsError
  ) {
    allShipmentAdjustments.isComplete = true
    allShipmentAdjustments.failedAdjustments = shipments
  }

  const hasFailures = allShipmentAdjustments.failedAdjustments.length > 0
  const hasRoutingConflicts =
    allShipmentAdjustments.routingConflictFailures.length > 0
  const hasOtherFailures = allShipmentAdjustments.otherFailures.length > 0

  // Get latest shipments
  useEffect(() => {
    if (allShipmentAdjustments.isComplete) {
      setSyncSettings({
        uid: Date.now(),
        checkVersion: false,
        hasRequiredEntitlements,
        failedModifiedShipments: allShipmentAdjustments.failedAdjustments,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allShipmentAdjustments.isComplete,
    hasRequiredEntitlements,
    setSyncSettings,
  ])

  useEffect(() => {
    if (
      syncShipmentsByPOsState.isComplete &&
      syncSettings.uid > 1 &&
      allShipmentAdjustments.failedAdjustments.length <= 0
    ) {
      setTimeout(() => {
        if (!isSearchTloSubmit) {
          invalidateQueriesAndClose()
        } else {
          const tloIds = tloShipmentAlerts.map((tlo) => tlo.data?.id)
          analyticsWrapper?.trackCustomEvent(
            'Submit & Go To TLO Related Shipments',
            'tlo ids',
            JSON.stringify(tloIds),
            'submit and go to TLO shipments from submit modal',
          )
          navigate(
            `/shipment-management?query={"truckLoadOptimizationRefIds":[${tloIds}]${
              vendorIds.length ? ',"vendorIds":[' + vendorIds + ']' : ''
            }}`,
          )
          navigate(0)
        }
      }, 1000)
    }
    // eslint-disable-next-line
  }, [allShipmentAdjustments, syncShipmentsByPOsState, syncSettings])

  useEffect(() => {
    if (shipments.length <= 0) {
      setTimeout(() => {
        handleClose()
      }, 100)
    }
    // eslint-disable-next-line
  }, [shipments])

  const handleOnSubmit = async (isSearchTlo = false) => {
    setIsSearchTloSubmit(isSearchTlo)
    setIsSubmitting(true)
    modifyShipments({
      shipments: shipments,
      uid: Date.now(),
      isTgtInternalAdmin: globalState.userInfo.isTgtInternalAdmin,
    })
    modifyShipmentsLoad({ shipments: shipments, uid: Date.now() })
    cancelShipments({ shipments: shipments, uid: Date.now() })
  }

  const handleViewTlo = (tloRefId: string) => {
    const query = {
      truckLoadOptimizationRefIds: [tloRefId],
      vendorIds: [],
      departmentIds: [],
      destinationIds: [],
      tmLocIds: [],
      reverseOrigins: [],
      purchaseOrders: [],
      purchaseOrder: '',
      shipmentIds: [],
      loadIds: [],
      bookingCategories: [],
      returnAuthorizationNumbers: [],
      reverseLogisticsReferenceNumbers: [],
      vendorLocationRelationship: [],
    }
    const searchUrl = `/shipment-management?query=${encodeURIComponent(
      JSON.stringify(query),
    )}`

    globalDispatch({
      payload: null,
      type: 'SET_MODAL',
    })

    navigate(searchUrl)
    navigate(0)
  }

  const foundShipmentWithBookingVersionInvalid = findInvalidBookingVersion(
    shipments,
    shipmentManagementState,
  )

  const hasInvalidBookingVersion = !!foundShipmentWithBookingVersionInvalid

  const shipmentsOnLoad = shipments.filter((shipment: any) => {
    return shipment.loadId
  })

  const hasShipmentsOnLoad = shipmentsOnLoad.length > 0

  const hasMissingCommodityTypes =
    shipments.filter((shipment: any) => {
      return !shipment.commodityCode
    }).length > 0

  const hasShipmentCancel =
    shipments.filter((shipment: any) => {
      return shipment.toBeCancelled
    }).length > 0

  return (
    <div className="hc-pa-normal" data-testid="shipment-checkout-modal">
      {hasInvalidBookingVersion && (
        <>
          <Heading size={5} className="hc-fs-xl">
            {isTloOnlyRemovalSubmit
              ? 'Shipments to Submit'
              : 'Shipments to Review and Submit'}
          </Heading>
          <br />
          <Heading size={6} className="hc-fs-lg">
            We detected newer shipment version(s) that may have conflicts with
            your modifications. Please review the shipments again and submit for
            update.
          </Heading>
          <br />
          <FlexBox justify="flex-start">
            <Button
              onClick={() => handleClose()}
              className="hc-mr-min"
              variant="outlined"
            >
              Review Again
            </Button>
          </FlexBox>
        </>
      )}
      {!hasInvalidBookingVersion && !hasFailures && (
        <>
          <TloWarnings
            alerts={tloShipmentAlerts
              .map((result) => result.data)
              .filter((alert): alert is TloAlert => !!alert)}
            onViewTlo={handleViewTlo}
            isLoading={tloShipmentAlerts.some((result) => result.isLoading)}
          />
          {tloShipmentAlerts
            .map((result) => result.data?.summary)
            .filter(
              (summary): summary is NonNullable<typeof summary> => !!summary,
            )
            .map((summary) => (
              <TloSummaryCard key={summary.tloRefId} summary={summary} />
            ))}
          <Heading size={5} className="hc-fs-xl">
            {isTloOnlyRemovalSubmit
              ? 'Shipments to Submit'
              : 'Shipments to Review and Submit'}
          </Heading>
          <span>Please verify that all modifications are correct.</span>
          <ModifiedShipmentsTable
            shipments={shipments}
            isTloOnlyRemovalSubmit={isTloOnlyRemovalSubmit}
            isTgtInternalAdmin={globalState.userInfo.isTgtInternalAdmin}
          />
          <br />
          {!hasMissingCommodityTypes && hasShipmentCancel && (
            <Acknowledgement hasShipmentsOnLoad={hasShipmentsOnLoad} />
          )}
          {hasMissingCommodityTypes && (
            <List>
              <List.Item className="hc-bg-error">
                One or more of the shipments being reviewed does not have a
                commodity code selected. Please select a commodity code for all
                shipments any try again.
              </List.Item>
            </List>
          )}
          <FlexBox justify="flex-end" style={{ marginTop: '12px' }}>
            <Button
              data-testid="shipment-checkout-cancel-button"
              onClick={() => handleClose()}
              className="hc-mr-sm"
              type="secondary"
              disabled={
                modifyShipmentsIsLoading ||
                modifyShipmentsLoadIsLoading ||
                cancelShipmentsIsLoading
              }
            >
              Cancel
            </Button>
            <Button
              data-testid="shipment-checkout-submit-changes-button"
              onClick={() => handleOnSubmit()}
              type="primary"
              isLoading={
                isSubmitting ||
                modifyShipmentsIsLoading ||
                modifyShipmentsLoadIsLoading ||
                cancelShipmentsIsLoading
              }
              disabled={hasMissingCommodityTypes}
            >
              Submit Changes
            </Button>
            {!!tloShipmentAlerts.filter((result) => result.data).length && (
              <Button
                className="hc-ml-sm"
                data-testid="checkout-submit-search-tlo-button"
                onClick={() => handleOnSubmit(true)}
                type="primary"
                isLoading={
                  modifyShipmentsIsLoading ||
                  modifyShipmentsLoadIsLoading ||
                  cancelShipmentsIsLoading
                }
                disabled={hasMissingCommodityTypes}
              >
                Submit and Go To TLO Related Shipments
              </Button>
            )}
          </FlexBox>
        </>
      )}
      {hasFailures && (
        <>
          <Heading size={5}>
            {isTloOnlyRemovalSubmit
              ? 'Shipments to Submit'
              : 'Shipments to Review and Submit'}
          </Heading>
          <br />

          {hasRoutingConflicts && (
            <>
              <ModifiedShipmentsTable
                shipments={allShipmentAdjustments.routingConflictFailures}
                isTloOnlyRemovalSubmit={isTloOnlyRemovalSubmit}
                isTgtInternalAdmin={globalState.userInfo.isTgtInternalAdmin}
              />
              <List>
                <List.Item
                  className="hc-bg-error hc-clr-contrast-weak"
                  data-testid="submit-changes-routing-conflict"
                >
                  {allShipmentAdjustments.routingConflictFailures.length === 1
                    ? `Shipment ${allShipmentAdjustments.routingConflictFailures[0].id} was not updated. Its status changed prior to completing your request and it is now assigned to load. To update this shipment, return to Shipment Management and resubmit your changes. Changes to any other shipments not shown here were submitted successfully.`
                    : `Shipments ${allShipmentAdjustments.routingConflictFailures.map((s) => s.id).join(', ')} were not updated. Their status changed prior to completing your request and they are now assigned to load. To update these shipments, return to Shipment Management and resubmit your changes. Changes to any other shipments not shown here were submitted successfully.`}
                </List.Item>
              </List>
              <br />
            </>
          )}

          {hasOtherFailures && (
            <>
              <ModifiedShipmentsTable
                shipments={allShipmentAdjustments.otherFailures}
                isTloOnlyRemovalSubmit={isTloOnlyRemovalSubmit}
                isTgtInternalAdmin={globalState.userInfo.isTgtInternalAdmin}
              />
              <List>
                <List.Item
                  className="hc-bg-error hc-clr-contrast-weak"
                  data-testid="submit-changes-failure"
                >
                  The Shipment Adjustments above failed to update. Please return
                  to Shipment Management and try again later.
                </List.Item>
              </List>
              <br />
            </>
          )}

          <FlexBox justify="flex-end" style={{ marginTop: '12px' }}>
            <Button
              data-testid="shipment-checkout-cancel-button"
              onClick={() => handleClose()}
              className="hc-mr-sm"
              type="secondary"
            >
              Close
            </Button>
          </FlexBox>
        </>
      )}
    </div>
  )
}

export default SubmitChanges
